import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { formatMapName } from '../../liveSession/lib/formatters';
import { formatStrategyType } from '../../strategies/lib/formatPlaybook';
import { isKnown } from '../../strategies/lib/playbook';
import PlaybookShell from '../../strategies/components/PlaybookShell';
import { listUtilityUses } from '../../strategies/strategyApi';
import type { UtilityUseRecord } from '../../strategies/types';
import { useMyTeam } from '../../team/useMyTeam';
import {
  addLineupToQueue,
  addRoleToQueue,
  clearDoneLineups,
  clearDoneRoles,
  getLearning,
  listPlayers,
  markLineupLearned,
  removeLineupFromQueue,
  removeRoleFromQueue,
  reorderLineupQueue,
  reorderRoleQueue,
  setStrategyQueued,
  unmarkLineupLearned,
} from '../learnApi';
import {
  ASSIGN_MARKS_NOTE,
  addedItemMessage,
  addedRolesMessage,
  catalogFiltersActive,
  clearDoneMessage,
  defaultAssignPlayerId,
  detailHref,
  emptyQueueCopy,
  filterLineups,
  filterStrategies,
  inMap,
  initialOpenStrategyIds,
  learnSearchString,
  lineupStats,
  listOwner,
  sourceSplit,
  sourceSplitText,
  mapChoices,
  moveIds,
  outsideNote,
  outsideOpenCount,
  parseLearnSearch,
  queuedRoleIds,
  removeLineupMessage,
  removeRoleMessage,
  removeRolesMessage,
  strategyStats,
  utilityLabel,
  viewingOwnMarks,
  type LearnSearch,
} from '../lib/learnView';
import type {
  LearningPageData,
  LineupQueueItem,
  QueueSource,
  RemovedLineup,
  RemovedRole,
  RoleQueueItem,
  TeamPlayer,
} from '../types';
import styles from './LearnPage.module.css';

const GRENADES = ['smoke', 'flash', 'he', 'molotov', 'decoy'];

function grenadeClass(type: string): string {
  if (type === 'smoke') return styles.smoke;
  if (type === 'flash') return styles.flash;
  if (type === 'molotov') return styles.molotov;
  if (type === 'he') return styles.he;
  if (type === 'decoy') return styles.decoy;
  return '';
}

type ToastState = { message: string; undo?: () => void };

export default function LearnPage() {
  const { user } = useAuth();
  const { team, loading: teamLoading } = useMyTeam(user?.id);
  const [params, setParams] = useSearchParams();
  const canAssign = team?.role === 'strategist';
  const search = parseLearnSearch(params, canAssign);
  const [learning, setLearning] = useState<LearningPageData | null>(null);
  const [lineups, setLineups] = useState<UtilityUseRecord[]>([]);
  const [players, setPlayers] = useState<TeamPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [doneOpen, setDoneOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [busy, setBusy] = useState(false);
  const seededFor = useRef<string | null>(null);

  const selectedPlayer = players.find((player) => player.id === search.playerId) ?? null;
  const waitingForPlayer = canAssign && search.asMode === 'assign' && !selectedPlayer;
  const targetPlayerId = search.asMode === 'assign' ? search.playerId : undefined;
  const assigning = search.asMode === 'assign';
  const ownMarks = viewingOwnMarks(search.asMode, selectedPlayer?.userId, user?.id);
  const playerName = assigning ? (selectedPlayer?.name || learning?.player.name || 'them') : 'you';
  const returnQuery = learnSearchString(search);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), toast.undo ? 5000 : 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!team?.id || !canAssign) return undefined;
    let cancelled = false;
    listPlayers(team.id)
      .then((rows) => { if (!cancelled) setPlayers(rows); })
      .catch(() => { if (!cancelled) setPlayers([]); });
    return () => { cancelled = true; };
  }, [team?.id, canAssign]);

  useEffect(() => {
    if (!canAssign || players.length === 0) return;
    setParams((current) => {
      const parsed = parseLearnSearch(current, true);
      if (parsed.asMode !== 'assign' || players.some((player) => player.id === parsed.playerId)) return current;
      const playerId = defaultAssignPlayerId(players, user?.id ?? '');
      if (!playerId) return current;
      return new URLSearchParams(learnSearchString({ ...parsed, playerId }));
    }, { replace: true });
  }, [canAssign, players, setParams, user?.id]);

  useEffect(() => {
    if (!team?.id) return undefined;
    let cancelled = false;
    listUtilityUses(team.id)
      .then((rows) => { if (!cancelled) setLineups(rows); })
      .catch(() => { if (!cancelled) setLineups([]); });
    return () => { cancelled = true; };
  }, [team?.id]);

  useEffect(() => {
    if (!team?.id || waitingForPlayer) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getLearning(team.id, targetPlayerId)
      .then((page) => { if (!cancelled) setLearning(page); })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load learning');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [team?.id, targetPlayerId, waitingForPlayer]);

  useEffect(() => {
    if (!learning) return;
    if (seededFor.current === learning.player.id) return;
    seededFor.current = learning.player.id;
    setOpenIds(new Set(initialOpenStrategyIds(learning.roleQueue)));
  }, [learning]);

  function writeSearch(next: LearnSearch) {
    const query = learnSearchString(next);
    setParams(new URLSearchParams(query), { replace: true });
  }

  function patchSearch(patch: Partial<LearnSearch>) {
    writeSearch({ ...search, ...patch });
  }

  const learnedIds = useMemo(
    () => new Set(learning?.learnedUtilityUseIds ?? []),
    [learning],
  );
  const strategies = learning?.strategies ?? [];
  const roleQueue = [...(learning?.roleQueue ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const lineupQueue = [...(learning?.lineupQueue ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const visibleLineups = useMemo(
    () => (canAssign ? lineups : lineups.map((lineup) => ({
      ...lineup,
      usedBy: lineup.usedBy.filter((use) => use.status === 'active'),
    }))),
    [canAssign, lineups],
  );
  const filteredStrategies = filterStrategies(strategies, search);
  const filteredLineups = filterLineups(visibleLineups, learnedIds, search);
  const scopedStrategies = strategies.filter((strategy) => inMap(strategy.map, search.map));
  const scopedLineups = visibleLineups.filter((lineup) => inMap(lineup.map, search.map));
  const roleStats = strategyStats(strategies, roleQueue, search.map);
  const throwStats = lineupStats(visibleLineups, learnedIds, lineupQueue, search.map);
  const stats = search.mode === 'lineups' ? throwStats : roleStats;
  const splitText = sourceSplitText(
    sourceSplit(
      search.mode === 'lineups'
        ? lineupQueue.map((item) => ({ map: item.map, learned: learnedIds.has(item.utilityUseId), source: item.source }))
        : roleQueue,
      search.map,
    ),
    { assigning, ownList: ownMarks, playerName },
  );
  const otherMaps = outsideNote(
    outsideOpenCount(
      search.mode === 'lineups'
        ? lineupQueue.map((item) => ({ map: item.map, learned: learnedIds.has(item.utilityUseId) }))
        : roleQueue,
      search.map,
    ),
    search.mode === 'lineups' ? 'lineup' : 'role',
  );
  const maps = mapChoices([...strategies.map((strategy) => strategy.map), ...visibleLineups.map((lineup) => lineup.map)]);
  const types = [...new Set(strategies.map((strategy) => strategy.strategyType).filter((type): type is string => Boolean(type)))].sort();

  const selectedLineup = useMemo(() => {
    const current = visibleLineups.find((lineup) => lineup.id === search.lineupId);
    if (current && inMap(current.map, search.map)) return current;
    const queued = lineupQueue
      .map((item) => visibleLineups.find((lineup) => lineup.id === item.utilityUseId))
      .find((lineup) => lineup && inMap(lineup.map, search.map));
    return queued || filteredLineups[0] || null;
  }, [filteredLineups, lineupQueue, search.lineupId, search.map, visibleLineups]);

  async function run(action: () => Promise<void>) {
    if (busy || !team?.id) return;
    setBusy(true);
    try {
      await action();
    } catch (err: unknown) {
      setToast({ message: err instanceof Error ? err.message : 'Could not update the list' });
    } finally {
      setBusy(false);
    }
  }

  function replaceRoles(roleQueueNext: RoleQueueItem[]) {
    setLearning((prev) => (prev ? { ...prev, roleQueue: roleQueueNext } : prev));
  }

  function replaceLineups(lineupQueueNext: LineupQueueItem[]) {
    setLearning((prev) => (prev ? { ...prev, lineupQueue: lineupQueueNext } : prev));
  }

  function undoRoles(removed: RemovedRole[]) {
    if (!team?.id) return;
    const sourceFor = (source: QueueSource) => (canAssign ? source : undefined);
    void run(async () => {
      let latest = learning?.roleQueue ?? [];
      for (const item of removed) {
        const result = await addRoleToQueue(team.id, item.strategyRoleId, targetPlayerId, sourceFor(item.source));
        latest = result.roleQueue;
      }
      replaceRoles(latest);
    });
  }

  function undoLineups(removed: RemovedLineup[]) {
    if (!team?.id) return;
    void run(async () => {
      let latest = learning?.lineupQueue ?? [];
      for (const item of removed) {
        const result = await addLineupToQueue(
          team.id,
          item.utilityUseId,
          targetPlayerId,
          canAssign ? item.source : undefined,
        );
        latest = result.lineupQueue;
      }
      replaceLineups(latest);
    });
  }

  function toggleRole(strategyId: string, strategyRoleId: string, roleName: string, strategyName: string) {
    const existing = roleQueue.find((item) => item.strategyRoleId === strategyRoleId);
    void run(async () => {
      if (!team?.id) return;
      if (existing) {
        const result = await removeRoleFromQueue(team.id, strategyRoleId, targetPlayerId);
        replaceRoles(result.roleQueue);
        const removed = result.removed ?? [];
        if (removed.length) {
          setToast({
            message: removeRoleMessage(roleName, strategyName, removed[0].source, assigning, playerName),
            undo: () => undoRoles(removed),
          });
        }
        return;
      }
      const result = await addRoleToQueue(team.id, strategyRoleId, targetPlayerId);
      replaceRoles(result.roleQueue);
      if (result.added) setToast({ message: addedItemMessage(roleName) });
      setOpenIds((prev) => new Set(prev).add(strategyId));
    });
  }

  function toggleAll(strategyId: string, strategyName: string, everyQueued: boolean) {
    void run(async () => {
      if (!team?.id) return;
      const result = await setStrategyQueued(team.id, strategyId, !everyQueued, targetPlayerId);
      replaceRoles(result.roleQueue);
      if (!everyQueued) {
        setToast({ message: addedRolesMessage(Number(result.added ?? 0), strategyName) });
        setOpenIds((prev) => new Set(prev).add(strategyId));
        return;
      }
      const removed = result.removed ?? [];
      if (!removed.length) return;
      setToast({
        message: removeRolesMessage(strategyName, removed, assigning),
        undo: () => undoRoles(removed),
      });
    });
  }

  function moveRole(strategyRoleId: string, direction: -1 | 1) {
    const visible = roleQueue.filter((item) => inMap(item.map, search.map) && !item.learned).map((item) => item.strategyRoleId);
    const next = moveIds(roleQueue.map((item) => item.strategyRoleId), visible, strategyRoleId, direction);
    if (!next || !team?.id) return;
    void run(async () => {
      const result = await reorderRoleQueue(team.id, next, targetPlayerId);
      replaceRoles(result.roleQueue);
    });
  }

  function moveLineup(utilityUseId: string, direction: -1 | 1) {
    const visible = lineupQueue
      .filter((item) => inMap(item.map, search.map) && !learnedIds.has(item.utilityUseId))
      .map((item) => item.utilityUseId);
    const next = moveIds(lineupQueue.map((item) => item.utilityUseId), visible, utilityUseId, direction);
    if (!next || !team?.id) return;
    void run(async () => {
      const result = await reorderLineupQueue(team.id, next, targetPlayerId);
      replaceLineups(result.lineupQueue);
    });
  }

  function toggleLineup(utilityUseId: string, name: string) {
    const existing = lineupQueue.find((item) => item.utilityUseId === utilityUseId);
    void run(async () => {
      if (!team?.id) return;
      if (existing) {
        const result = await removeLineupFromQueue(team.id, utilityUseId, targetPlayerId);
        replaceLineups(result.lineupQueue);
        const removed = result.removed ?? [];
        if (removed.length) {
          setToast({
            message: removeLineupMessage(name, removed[0].source, assigning),
            undo: () => undoLineups(removed),
          });
        }
        return;
      }
      const result = await addLineupToQueue(team.id, utilityUseId, targetPlayerId);
      replaceLineups(result.lineupQueue);
      if (result.added) setToast({ message: addedItemMessage(name) });
    });
  }

  function toggleLineupLearned(utilityUseId: string, name: string, learned: boolean) {
    void run(async () => {
      if (!team?.id || !ownMarks) return;
      const result = learned
        ? await unmarkLineupLearned(team.id, utilityUseId)
        : await markLineupLearned(team.id, utilityUseId);
      setLearning((prev) => {
        if (!prev) return prev;
        const ids = result.learned
          ? [...new Set([...prev.learnedUtilityUseIds, result.utilityUseId])]
          : prev.learnedUtilityUseIds.filter((id) => id !== result.utilityUseId);
        return {
          ...prev,
          learnedUtilityUseIds: ids,
          lineupQueue: prev.lineupQueue.map((item) => (
            item.utilityUseId === result.utilityUseId ? { ...item, learned: result.learned } : item
          )),
        };
      });
      setToast({ message: `${result.learned ? 'Marked' : 'Cleared'} ${name} as learned for you.` });
    });
  }

  function clearDone() {
    void run(async () => {
      if (!team?.id) return;
      const map = search.map === 'all' ? undefined : search.map;
      if (search.mode === 'lineups') {
        const result = await clearDoneLineups(team.id, map, targetPlayerId);
        replaceLineups(result.lineupQueue);
        const cleared = result.cleared ?? [];
        if (!cleared.length) return;
        setToast({
          message: clearDoneMessage(cleared.length, 'throw'),
          undo: () => undoLineups(cleared),
        });
        return;
      }
      const result = await clearDoneRoles(team.id, map, targetPlayerId);
      replaceRoles(result.roleQueue);
      const cleared = result.cleared ?? [];
      if (!cleared.length) return;
      setToast({
        message: clearDoneMessage(cleared.length, 'role'),
        undo: () => undoRoles(cleared),
      });
    });
  }

  const knownLabel = !assigning || selectedPlayer?.userId === user?.id
    ? 'Known to you'
    : `Known to ${playerName}`;
  const ownerList = listOwner(assigning, playerName);

  return (
    <PlaybookShell crumb="Learn">
      <div className={styles.head}>
        <div>
          <div className={styles.eyebrow}>Your practice</div>
          <h1>Learn</h1>
          <p>
            {search.mode === 'lineups'
              ? 'Practice throws on their own, apart from the strategies that use them.'
              : 'Work through the roles on your list, or add a whole strategy from the playbook.'}
          </p>
        </div>
        <div className={styles.headActions}>
          <div className={styles.segmented} role="group" aria-label="What you are learning">
            <button type="button" aria-pressed={search.mode === 'strategies'} onClick={() => patchSearch({ mode: 'strategies' })}>Strategies</button>
            <button type="button" aria-pressed={search.mode === 'lineups'} onClick={() => patchSearch({ mode: 'lineups' })}>Lineups</button>
          </div>
          {canAssign && (
            <div className={styles.segmented} role="group" aria-label="Who is editing the list">
              <button type="button" aria-pressed={!assigning} onClick={() => patchSearch({ asMode: 'play', playerId: '' })}>Playing</button>
              <button
                type="button"
                aria-pressed={assigning}
                onClick={() => patchSearch({
                  asMode: 'assign',
                  playerId: search.playerId || defaultAssignPlayerId(players, user?.id ?? ''),
                })}
              >
                Assigning
              </button>
            </div>
          )}
          {canAssign && assigning && (
            <select
              className={styles.playerPick}
              aria-label="Player to assign"
              value={search.playerId}
              onChange={(event) => patchSearch({ playerId: event.target.value })}
            >
              {players.map((player) => (
                <option key={player.id} value={player.id}>
                  {player.userId === user?.id ? `${player.name} (you)` : player.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {assigning && selectedPlayer && (
        <div className={styles.assignBanner}>
          {selectedPlayer.userId === user?.id
            ? `Assigning practice for ${selectedPlayer.name}. This is the list you see while playing. These ${search.mode === 'lineups' ? 'throws' : 'roles'} stay on it.`
            : `Assigning practice for ${selectedPlayer.name}. They will see these ${search.mode === 'lineups' ? 'throws' : 'roles'} on their Learn page.`}
        </div>
      )}

      <section className={styles.stats} aria-label="Your learning summary">
        <div className={`${styles.stat} ${styles.knownStat}`}>
          <div>
            <div className={styles.micro}>{knownLabel}</div>
            <p>{search.mode === 'lineups' ? 'Throws marked learned' : 'All roles learned'}</p>
          </div>
          <strong>{stats.known}</strong>
        </div>
        <div className={styles.stat}>
          <div>
            <div className={styles.micro}>Map coverage</div>
            <p>{stats.coverage}</p>
          </div>
          <strong>{stats.percent}%</strong>
        </div>
        <div className={`${styles.stat} ${styles.pendingStat}`}>
          <div>
            <div className={styles.micro}>To learn</div>
            <p>{splitText}</p>
          </div>
          <strong>{stats.open}</strong>
        </div>
      </section>

      <div className={styles.scopeBar}>
        <label>
          Map
          <select aria-label="Map" value={maps.some((map) => map.value === search.map) ? search.map : 'all'} onChange={(event) => patchSearch({ map: event.target.value })}>
            <option value="all">All maps</option>
            {maps.map((map) => <option key={map.value} value={map.value}>{map.label}</option>)}
          </select>
        </label>
        <p role="status">{otherMaps}</p>
      </div>

      {(teamLoading || loading) && <p className={styles.status}>Loading practice…</p>}
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.grid}>
        <section className={styles.panel} aria-label="To-learn list">
          <div className={styles.panelHead}>
            <div>
              <h2>{search.mode === 'lineups' ? 'Throws to learn' : 'To learn'}</h2>
              <p>{splitText}</p>
            </div>
            <span className={styles.micro}>{stats.open} OPEN</span>
          </div>
          <QueueBody
            mode={search.mode}
            roleQueue={roleQueue.filter((item) => inMap(item.map, search.map))}
            lineupQueue={lineupQueue.filter((item) => inMap(item.map, search.map))}
            learnedIds={learnedIds}
            returnQuery={returnQuery}
            empty={emptyQueueCopy(search.mode, assigning, playerName)}
            doneOpen={doneOpen}
            onDoneOpen={setDoneOpen}
            onMoveRole={moveRole}
            onRemoveRole={(item) => toggleRole(item.strategyId, item.strategyRoleId, item.roleName, item.strategyName)}
            onMoveLineup={moveLineup}
            onRemoveLineup={(item) => toggleLineup(item.utilityUseId, item.name)}
            onSelectLineup={(id) => patchSearch({ mode: 'lineups', lineupId: id })}
            onClearDone={clearDone}
          />
        </section>

        <section className={styles.panel} aria-label="Playbook">
          <div className={styles.filters}>
            <div className={styles.filterTop}>
              <div className={styles.search}>
                <input
                  type="search"
                  aria-label="Search the playbook"
                  placeholder={search.mode === 'lineups' ? 'Search by throw, note or ID…' : 'Search by strategy, callout or ID…'}
                  value={search.q}
                  onChange={(event) => patchSearch({ q: event.target.value })}
                />
              </div>
              <div className={styles.segmented} role="group" aria-label="Knowledge">
                <button type="button" aria-pressed={search.knowledge === 'all'} onClick={() => patchSearch({ knowledge: 'all' })}>
                  {search.mode === 'lineups' ? 'All lineups' : 'All strategies'}
                </button>
                <button type="button" aria-pressed={search.knowledge === 'known'} onClick={() => patchSearch({ knowledge: 'known' })}>Known</button>
                <button type="button" aria-pressed={search.knowledge === 'pending'} onClick={() => patchSearch({ knowledge: 'pending' })}>Not yet known</button>
              </div>
            </div>
            <div className={styles.filterBottom}>
              {search.mode === 'strategies' && (
                <>
                  <label>
                    Type
                    <select aria-label="Type" value={search.type} onChange={(event) => patchSearch({ type: event.target.value })}>
                      <option value="all">All types</option>
                      {types.map((type) => <option key={type} value={type}>{formatStrategyType(type)}</option>)}
                    </select>
                  </label>
                  <label>
                    Side
                    <select aria-label="Side" value={search.side} onChange={(event) => patchSearch({ side: event.target.value as LearnSearch['side'] })}>
                      <option value="all">Both sides</option>
                      <option value="T">T</option>
                      <option value="CT">CT</option>
                    </select>
                  </label>
                </>
              )}
              {search.mode === 'lineups' && (
                <label>
                  Grenade
                  <select aria-label="Grenade" value={search.grenade} onChange={(event) => patchSearch({ grenade: event.target.value })}>
                    <option value="all">All grenades</option>
                    {GRENADES.map((grenade) => <option key={grenade} value={grenade}>{utilityLabel(grenade)}</option>)}
                  </select>
                </label>
              )}
              <button
                type="button"
                className={styles.clear}
                disabled={!catalogFiltersActive(search)}
                onClick={() => patchSearch({ q: '', type: 'all', side: 'all', grenade: 'all', knowledge: 'all' })}
              >
                Reset filters
              </button>
            </div>
          </div>
          <div className={styles.results} role="status">
            {search.mode === 'lineups'
              ? `${filteredLineups.length} of ${scopedLineups.length} lineups`
              : `${filteredStrategies.length} of ${scopedStrategies.length} strategies`}
          </div>
          {search.mode === 'lineups' && selectedLineup && (
            <article className={styles.inspect} aria-label="Lineup detail">
              <div className={styles.inspectMeta}>
                <span className={styles.eyebrow}>{formatMapName(selectedLineup.map) || selectedLineup.map}</span>
                <span className={`${styles.badge} ${grenadeClass(selectedLineup.utilityType)}`}>{utilityLabel(selectedLineup.utilityType)}</span>
                {learnedIds.has(selectedLineup.id)
                  ? <span className={`${styles.badge} ${styles.known}`}>Learned</span>
                  : <span className={`${styles.badge} ${styles.pending}`}>Not yet known</span>}
              </div>
              <h2>{selectedLineup.name}</h2>
              <p>{selectedLineup.description || 'No notes yet.'}</p>
              {selectedLineup.url
                ? <p><a href={selectedLineup.url} target="_blank" rel="noopener noreferrer">Open learning link</a></p>
                : <p>No learning link yet.</p>}
              <h3>Used by</h3>
              {selectedLineup.usedBy.length > 0 ? (
                <ul className={styles.usedList}>
                  {selectedLineup.usedBy.map((use) => (
                    <li key={use.strategyId}><Link to={detailHref(use.strategyId, returnQuery)}>{use.strategyName}</Link></li>
                  ))}
                </ul>
              ) : <p>No strategy points here.</p>}
              <div className={styles.inspectActions}>
                <button
                  type="button"
                  className={styles.queueToggle}
                  aria-pressed={lineupQueue.some((item) => item.utilityUseId === selectedLineup.id)}
                  onClick={() => toggleLineup(selectedLineup.id, selectedLineup.name)}
                >
                  {lineupQueue.some((item) => item.utilityUseId === selectedLineup.id) ? `On ${ownerList}` : `Add to ${ownerList}`}
                </button>
                {ownMarks && (
                  <button
                    type="button"
                    aria-pressed={learnedIds.has(selectedLineup.id)}
                    onClick={() => toggleLineupLearned(selectedLineup.id, selectedLineup.name, learnedIds.has(selectedLineup.id))}
                  >
                    {learnedIds.has(selectedLineup.id) ? '✓ Learned · Undo' : 'Mark learned'}
                  </button>
                )}
              </div>
              {!ownMarks && <p className={styles.note}>{ASSIGN_MARKS_NOTE}</p>}
            </article>
          )}
          {search.mode === 'strategies' ? (
            filteredStrategies.length === 0 ? (
              <EmptyCatalog noun="strategies" onReset={() => patchSearch({ q: '', type: 'all', side: 'all', grenade: 'all', knowledge: 'all' })} />
            ) : filteredStrategies.map((strategy) => {
              const queued = queuedRoleIds(roleQueue, strategy.id);
              const every = strategy.roleCount > 0 && strategy.roles.every((role) => queued.has(role.id));
              const open = openIds.has(strategy.id);
              const learned = strategy.roles.filter((role) => role.learned).length;
              const known = isKnown(strategy);
              return (
                <article className={styles.call} key={strategy.id}>
                  <div className={styles.callHead}>
                    <button
                      type="button"
                      className={styles.callToggle}
                      aria-expanded={open}
                      onClick={() => setOpenIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(strategy.id)) next.delete(strategy.id);
                        else next.add(strategy.id);
                        return next;
                      })}
                    >
                      <strong>{strategy.name}</strong>
                      <span className={styles.micro}>{formatMapName(strategy.map) || strategy.map}</span>
                      <span className={`${styles.badge} ${strategy.side.toUpperCase() === 'CT' ? styles.ct : styles.t}`}>{strategy.side.toUpperCase()}</span>
                      {strategy.strategyType && <span className={styles.badge}>{formatStrategyType(strategy.strategyType)}</span>}
                      <span className={`${styles.badge} ${known ? styles.known : styles.pending}`}>
                        {known ? '✓ Known' : learned ? '◒ Learning' : '○ Not yet known'}
                      </span>
                      <span className={styles.ticks} aria-hidden="true">
                        {strategy.roles.map((role) => <i key={role.id} className={role.learned ? styles.done : ''} />)}
                      </span>
                      <span className={styles.micro}>{learned}/{strategy.roleCount}</span>
                    </button>
                    <div className={styles.callActions}>
                      {queued.size > 0 && <span className={styles.queueCount}>{queued.size} on the list</span>}
                      <button
                        type="button"
                        className={styles.queueToggle}
                        aria-pressed={every}
                        aria-label={every ? `Remove all roles for ${strategy.name} from the list` : `Add all roles for ${strategy.name} to the list`}
                        onClick={() => toggleAll(strategy.id, strategy.name, every)}
                      >
                        All roles
                      </button>
                      <Link className={styles.queueToggle} to={detailHref(strategy.id, returnQuery)}>Open</Link>
                    </div>
                  </div>
                  {open && (
                    <ul className={styles.roleQueue}>
                      {strategy.roles.map((role) => {
                        const onList = queued.has(role.id);
                        return (
                          <li key={role.id}>
                            <span className={styles.todoCopy}>
                              <strong>{role.name}</strong>
                              {role.learned && <span className={`${styles.badge} ${styles.known}`}>Learned</span>}
                            </span>
                            <button
                              type="button"
                              className={styles.queueToggle}
                              aria-pressed={onList}
                              aria-label={onList ? `Remove ${role.name} from the list` : `Add ${role.name} to the list`}
                              onClick={() => toggleRole(strategy.id, role.id, role.name, strategy.name)}
                            >
                              {onList ? 'On list' : 'Add'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </article>
              );
            })
          ) : (
            filteredLineups.length === 0 ? (
              <EmptyCatalog noun="lineups" onReset={() => patchSearch({ q: '', type: 'all', side: 'all', grenade: 'all', knowledge: 'all' })} />
            ) : filteredLineups.map((lineup) => {
              const onList = lineupQueue.some((item) => item.utilityUseId === lineup.id);
              return (
                <div key={lineup.id} className={`${styles.lineupRow} ${selectedLineup?.id === lineup.id ? styles.isSelected : ''}`}>
                  <button type="button" className={styles.lineupSelect} aria-pressed={selectedLineup?.id === lineup.id} onClick={() => patchSearch({ lineupId: lineup.id })}>
                    <strong>{lineup.name}</strong>
                    <span className={styles.micro}>{formatMapName(lineup.map) || lineup.map}</span>
                    <span className={`${styles.badge} ${grenadeClass(lineup.utilityType)}`}>{utilityLabel(lineup.utilityType)}</span>
                    {learnedIds.has(lineup.id)
                      ? <span className={`${styles.badge} ${styles.known}`}>Learned</span>
                      : <span className={`${styles.badge} ${styles.pending}`}>Not yet known</span>}
                  </button>
                  <button
                    type="button"
                    className={styles.queueToggle}
                    aria-pressed={onList}
                    aria-label={onList ? `Remove ${lineup.name} from the list` : `Add ${lineup.name} to the list`}
                    onClick={() => toggleLineup(lineup.id, lineup.name)}
                  >
                    {onList ? 'On list' : 'Add'}
                  </button>
                </div>
              );
            })
          )}
        </section>
      </div>

      <div className={styles.footer}>
        <span>Queued work is practice. Learned marks are what you already know.</span>
      </div>
      {toast && (
        <div className={styles.toast} role="status">
          <span>{toast.message}</span>
          {toast.undo && <button type="button" onClick={() => { const undo = toast.undo; setToast(null); undo?.(); }}>Undo</button>}
        </div>
      )}
    </PlaybookShell>
  );
}

function EmptyCatalog({ noun, onReset }: { noun: string; onReset: () => void }) {
  return (
    <div className={styles.empty}>
      <div className={styles.eyebrow}>No matching {noun === 'lineups' ? 'throws' : 'calls'}</div>
      <h2>No {noun} found</h2>
      <p>Try a different search or broaden your filters.</p>
      <button type="button" onClick={onReset}>Reset all filters</button>
    </div>
  );
}

function QueueBody({
  mode,
  roleQueue,
  lineupQueue,
  learnedIds,
  returnQuery,
  empty,
  doneOpen,
  onDoneOpen,
  onMoveRole,
  onRemoveRole,
  onMoveLineup,
  onRemoveLineup,
  onSelectLineup,
  onClearDone,
}: {
  mode: LearnSearch['mode'];
  roleQueue: RoleQueueItem[];
  lineupQueue: LineupQueueItem[];
  learnedIds: Set<string>;
  returnQuery: string;
  empty: string;
  doneOpen: boolean;
  onDoneOpen: (open: boolean) => void;
  onMoveRole: (id: string, direction: -1 | 1) => void;
  onRemoveRole: (item: RoleQueueItem) => void;
  onMoveLineup: (id: string, direction: -1 | 1) => void;
  onRemoveLineup: (item: LineupQueueItem) => void;
  onSelectLineup: (id: string) => void;
  onClearDone: () => void;
}) {
  if (mode === 'lineups') {
    const active = lineupQueue.filter((item) => !learnedIds.has(item.utilityUseId));
    const done = lineupQueue.filter((item) => learnedIds.has(item.utilityUseId));
    if (!lineupQueue.length) return <EmptyQueue copy={empty} />;
    return (
      <>
        {active.length === 0 && <p className={styles.emptyInline}>Nothing left to practice in this view.</p>}
        {active.length > 0 && (
          <ol className={styles.todoList}>
            {active.map((item, index) => (
              <LineupRow key={item.id} item={item} index={index} total={active.length} learned={false} onMove={onMoveLineup} onRemove={onRemoveLineup} onOpen={onSelectLineup} movable />
            ))}
          </ol>
        )}
        {done.length > 0 && (
          <details className={styles.doneFold} open={doneOpen} onToggle={(event) => onDoneOpen(event.currentTarget.open)}>
            <summary>Done · {done.length}</summary>
            <ol className={styles.todoList}>
              {done.map((item) => (
                <LineupRow key={item.id} item={item} index={0} total={1} learned onMove={onMoveLineup} onRemove={onRemoveLineup} onOpen={onSelectLineup} movable={false} />
              ))}
            </ol>
            <button type="button" className={styles.clear} onClick={onClearDone}>Clear done</button>
          </details>
        )}
      </>
    );
  }

  const active = roleQueue.filter((item) => !item.learned);
  const done = roleQueue.filter((item) => item.learned);
  if (!roleQueue.length) return <EmptyQueue copy={empty} />;
  return (
    <>
      {active.length === 0 && <p className={styles.emptyInline}>Nothing left to practice in this view.</p>}
      {groupQueue(active).map((group) => (
        <section className={styles.todoGroup} key={`${group.strategyId}-open`}>
          <header>
            <Link to={detailHref(group.strategyId, returnQuery)}>{group.strategyName}</Link>
            <span className={`${styles.badge} ${group.side.toUpperCase() === 'CT' ? styles.ct : styles.t}`}>{group.side.toUpperCase()}</span>
            <span className={styles.micro}>{formatMapName(group.map) || group.map}</span>
          </header>
          <ol className={styles.todoList}>
            {group.items.map((item) => {
              const index = active.findIndex((row) => row.strategyRoleId === item.strategyRoleId);
              return (
                <RoleRow key={item.id} item={item} index={index} total={active.length} returnQuery={returnQuery} onMove={onMoveRole} onRemove={onRemoveRole} movable />
              );
            })}
          </ol>
        </section>
      ))}
      {done.length > 0 && (
        <details className={styles.doneFold} open={doneOpen} onToggle={(event) => onDoneOpen(event.currentTarget.open)}>
          <summary>Done · {done.length}</summary>
          {groupQueue(done).map((group) => (
            <section className={styles.todoGroup} key={`${group.strategyId}-done`}>
              <header>
                <Link to={detailHref(group.strategyId, returnQuery)}>{group.strategyName}</Link>
                <span className={styles.micro}>{formatMapName(group.map) || group.map}</span>
              </header>
              <ol className={styles.todoList}>
                {group.items.map((item) => (
                  <RoleRow key={item.id} item={item} index={0} total={1} returnQuery={returnQuery} onMove={onMoveRole} onRemove={onRemoveRole} movable={false} />
                ))}
              </ol>
            </section>
          ))}
          <button type="button" className={styles.clear} onClick={onClearDone}>Clear done</button>
        </details>
      )}
    </>
  );
}

function EmptyQueue({ copy }: { copy: string }) {
  return (
    <div className={styles.emptyInline}>
      <div className={styles.eyebrow}>Practice queue</div>
      <h2>Nothing to learn yet</h2>
      <p>{copy}</p>
    </div>
  );
}

function groupQueue(items: RoleQueueItem[]) {
  const groups: { strategyId: string; strategyName: string; map: string; side: string; items: RoleQueueItem[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (!last || last.strategyId !== item.strategyId) {
      groups.push({ strategyId: item.strategyId, strategyName: item.strategyName, map: item.map, side: item.side, items: [item] });
    } else {
      last.items.push(item);
    }
  }
  return groups;
}

function RoleRow({
  item, index, total, returnQuery, movable, onMove, onRemove,
}: {
  item: RoleQueueItem;
  index: number;
  total: number;
  returnQuery: string;
  movable: boolean;
  onMove: (id: string, direction: -1 | 1) => void;
  onRemove: (item: RoleQueueItem) => void;
}) {
  return (
    <li className={styles.todoItem}>
      <div className={styles.todoCopy}>
        <strong>{item.roleName}</strong>
        <span className={`${styles.badge} ${item.source === 'assigned' ? styles.pending : ''}`}>{item.source === 'assigned' ? 'Assigned' : 'You added'}</span>
        {item.learned && <span className={`${styles.badge} ${styles.known}`}>Learned</span>}
      </div>
      <div className={styles.todoActions}>
        {movable && (
          <>
            <button type="button" aria-label={`Move ${item.roleName} up`} disabled={index === 0} onClick={() => onMove(item.strategyRoleId, -1)}>↑</button>
            <button type="button" aria-label={`Move ${item.roleName} down`} disabled={index === total - 1} onClick={() => onMove(item.strategyRoleId, 1)}>↓</button>
          </>
        )}
        <Link to={detailHref(item.strategyId, returnQuery, item.strategyRoleId)}>Open</Link>
        <button type="button" aria-label={`Remove ${item.roleName} from the list`} onClick={() => onRemove(item)}>Remove</button>
      </div>
    </li>
  );
}

function LineupRow({
  item, index, total, learned, movable, onMove, onRemove, onOpen,
}: {
  item: LineupQueueItem;
  index: number;
  total: number;
  learned: boolean;
  movable: boolean;
  onMove: (id: string, direction: -1 | 1) => void;
  onRemove: (item: LineupQueueItem) => void;
  onOpen: (id: string) => void;
}) {
  return (
    <li className={styles.todoItem}>
      <div className={styles.todoCopy}>
        <strong>{item.name}</strong>
        <span className={`${styles.badge} ${grenadeClass(item.utilityType)}`}>{utilityLabel(item.utilityType)}</span>
        <span className={styles.micro}>{formatMapName(item.map) || item.map}</span>
        <span className={`${styles.badge} ${item.source === 'assigned' ? styles.pending : ''}`}>{item.source === 'assigned' ? 'Assigned' : 'You added'}</span>
        {learned && <span className={`${styles.badge} ${styles.known}`}>Learned</span>}
      </div>
      <div className={styles.todoActions}>
        {movable && (
          <>
            <button type="button" aria-label={`Move ${item.name} up`} disabled={index === 0} onClick={() => onMove(item.utilityUseId, -1)}>↑</button>
            <button type="button" aria-label={`Move ${item.name} down`} disabled={index === total - 1} onClick={() => onMove(item.utilityUseId, 1)}>↓</button>
          </>
        )}
        <button type="button" onClick={() => onOpen(item.utilityUseId)}>Open</button>
        <button type="button" aria-label={`Remove ${item.name} from the list`} onClick={() => onRemove(item)}>Remove</button>
      </div>
    </li>
  );
}
